import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { distinctUntilObjectChanged } from '@zero/rxjs-operator';
import { PushQuoteService } from '@zero/shared/ui-push';
import { StoreErrorHandler } from '@zero/shared/util-global-state';
import { Quote } from '@zero/shared/util-models';
import { AssetSearchService } from '@zero/shared/util-services';
import { isEmpty } from 'lodash-es';
import { catchError, distinctUntilChanged, filter, map, of, switchMap, tap } from 'rxjs';
import { DiscoverIndexVM } from '../entities/discover-index.entity';
import { DiscoverInstrument, DiscoverInstrumentVM } from '../entities/discover-instrument.entity';
import {
  DiscoverListAndLinksDetails,
  DiscoverListAndLinksDetailsInstrumentVM,
  DiscoverListAndLinksDetailsVM,
  DiscoverListsAndLinksVM,
} from '../entities/discover-lists-and-links.entity';
import { DiscoverOverview, DiscoverOverviewVM } from '../entities/discover-overview.entity';
import { DiscoverTeaserVM } from '../entities/discover-teaser.entity';
import { DiscoverTopPerformerGroup, DiscoverTopPerformerGroupVM } from '../entities/discover-top-performer-group.entity';
import { DiscoverTopPerformerInstrumentVM } from '../entities/discover-top-performer-instrument.entity';
import { DiscoverInfrastructure } from '../infrastructure/discover.infrastructure';
import { discoverActions, discoverApiActions } from './discover.actions';
import { discoverFeature } from './discover.reducer';
import {
  selectDiscoverDetailsInstrumentISINs,
  selectDiscoverDetailsInstruments,
  selectDiscoverIndices,
  selectDiscoverIndicesQuoteISINs,
  selectDiscoverListDetailsInstrumentISINs,
  selectDiscoverTopPerformerInstrumentISINsByGroup,
} from './discover.selectors';

@Injectable({ providedIn: 'root' })
export class DiscoverEffects {
  readonly #store = inject(Store);
  readonly #actions$ = inject(Actions);
  readonly #discoverInfrastructure = inject(DiscoverInfrastructure);
  readonly #pushService = inject(PushQuoteService);
  readonly #storeErrorHandler = inject(StoreErrorHandler);
  readonly #assetSearchService = inject(AssetSearchService);

  public loadDiscoverOverview$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(discoverActions.loadDiscoverOverview),
      concatLatestFrom(() => this.#store.select(discoverFeature.selectOverviewInitiallyLoaded)),
      filter(([{ force }, loaded]) => !loaded || force),
      tap(() => this.#store.dispatch(discoverActions.setOverviewLoading({ loading: true }))),
      switchMap(() =>
        this.#discoverInfrastructure.getDiscoverOverview().pipe(
          map((response) => this.mapDiscoverOverviewToVm(response)),
          map((overview) => discoverApiActions.loadDiscoverOverviewSuccess({ overview })),
          catchError((error) => of(discoverApiActions.loadDiscoverOverviewFailure({ error }))),
        ),
      ),
    );
  });

  public subscribeToIndicesQuotes$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(discoverActions.subscribeToIndices),
      switchMap(() =>
        this.#store.select(selectDiscoverIndicesQuoteISINs).pipe(
          filter((keys) => keys.length > 0),
          distinctUntilObjectChanged(),
        ),
      ),
      switchMap((isins) =>
        this.#pushService.subscribeMultiBuffered(isins).pipe(
          filter((quotes) => !isEmpty(quotes)),
          concatLatestFrom(() => this.#store.select(selectDiscoverIndices)),
          map(([quotes, indices]) =>
            indices.map((index) => mapQuoteToInstrument<DiscoverIndexVM>(quotes[index.historicQuote.isin], index)).filter((index) => index),
          ),
          map((indices) => discoverActions.updateIndicesQuotes({ indices })),
          catchError(() => of(discoverActions.subscribeToIndicesFailure())),
        ),
      ),
    );
  });

  public unsubscribeFromIndicesQuotes$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(discoverActions.unsubscribeFromIndices),
        concatLatestFrom(() => this.#store.select(selectDiscoverIndicesQuoteISINs)),
        tap(([, isins]) => this.#pushService.unsubscribeMulti(isins)),
      );
    },
    { dispatch: false },
  );

  public loadDiscoverTodayDetails$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(discoverActions.loadDiscoverTodayDetails),
      switchMap(({ id }) =>
        this.#discoverInfrastructure.getDiscoverTodayDetails(id).pipe(
          map((response) => ({
            ...response,
            instruments: this.mapDiscoverInstrumentsToRecord(response?.instruments),
          })),
          map((response) => discoverApiActions.loadDiscoverArticleDetailsSuccess({ details: response })),
          catchError((error) => of(discoverApiActions.loadDiscoverArticleDetailsFailure({ error }))),
        ),
      ),
    );
  });

  public loadDiscoverKnowHowDetails$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(discoverActions.loadDiscoverKnowHowDetails),
      switchMap(({ id }) =>
        this.#discoverInfrastructure.getDiscoverKnowHowDetails(id).pipe(
          map((response) => ({
            ...response,
            instruments: this.mapDiscoverInstrumentsToRecord(response?.instruments),
          })),
          map((response) => discoverApiActions.loadDiscoverArticleDetailsSuccess({ details: response })),
          catchError((error) => of(discoverApiActions.loadDiscoverArticleDetailsFailure({ error }))),
        ),
      ),
    );
  });

  public loadDiscoverMegatrendDetails$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(discoverActions.loadDiscoverMegatrendsDetails),
      switchMap(({ id }) =>
        this.#discoverInfrastructure.getDiscoverMegatrendDetails(id).pipe(
          map((response) => ({
            ...response,
            instruments: this.mapDiscoverInstrumentsToRecord(response?.instruments),
          })),
          map((response) => discoverApiActions.loadDiscoverArticleDetailsSuccess({ details: response })),
          catchError((error) => of(discoverApiActions.loadDiscoverArticleDetailsFailure({ error }))),
        ),
      ),
    );
  });

  public subscribeToDetailsInstruments$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(discoverActions.subscribeToDetailsInstruments),
      switchMap(() =>
        this.#store
          .select(selectDiscoverDetailsInstrumentISINs)
          .pipe(distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))),
      ),
      switchMap((isins) =>
        this.#pushService.subscribeMultiBuffered(isins).pipe(
          filter((quotes) => !isEmpty(quotes)),
          concatLatestFrom(() => this.#store.select(selectDiscoverDetailsInstruments)),
          map(([quotes, instruments]) =>
            Object.values(instruments)
              .map((instrument) => mapQuoteToInstrument<DiscoverInstrumentVM>(quotes[instrument.isin], instrument))
              .filter((instrument) => instrument),
          ),
          map((instruments) => discoverActions.updateDetailsInstrumentsQuotes({ instruments })),
          catchError(() => of(discoverActions.subscribeToDetailsInstrumentsFailure())),
        ),
      ),
    );
  });

  public unsubscribeFromDetailsInstruments$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(discoverActions.unsubscribeFromDetailsInstruments),
        concatLatestFrom(() => this.#store.select(selectDiscoverDetailsInstrumentISINs)),
        tap(([, isins]) => this.#pushService.unsubscribeMulti(isins)),
      );
    },
    { dispatch: false },
  );

  public loadDiscoverListAndLinksDetails$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(discoverActions.loadDiscoverListDetails),
      switchMap(({ id }) =>
        this.#discoverInfrastructure.getDiscoverListDetails(id).pipe(
          map((response) => this.mapInstrumentListToVM(response)),
          map((details) => discoverApiActions.loadDiscoverListDetailsSuccess({ details })),
          catchError((error) => of(discoverApiActions.loadDiscoverListDetailsFailure({ error }))),
        ),
      ),
    );
  });

  public subscribeToListInstruments$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(discoverActions.subscribeToListInstruments),
      switchMap(() =>
        this.#store
          .select(selectDiscoverListDetailsInstrumentISINs)
          .pipe(distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))),
      ),
      switchMap((isins) =>
        this.#pushService.subscribeMultiBuffered(isins).pipe(
          filter((quotes) => !isEmpty(quotes)),
          concatLatestFrom(() => this.#store.select(discoverFeature.selectListDetails)),
          map(([quotes, list]) =>
            Object.values(list.instruments)
              .map((instrument) => mapQuoteToInstrument<DiscoverInstrumentVM>(quotes[instrument.isin], instrument))
              .filter((instrument) => instrument),
          ),
          map((instruments) => discoverActions.updateListInstrumentsQuotes({ instruments })),
          catchError(() => of(discoverActions.subscribeToListInstrumentsFailure())),
        ),
      ),
    );
  });

  public unsubscribeFromListAndLinksInstruments$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(discoverActions.unsubscribeFromListInstruments),
        concatLatestFrom(() => this.#store.select(selectDiscoverListDetailsInstrumentISINs)),
        tap(([, isins]) => this.#pushService.unsubscribeMulti(isins)),
      );
    },
    { dispatch: false },
  );

  private mapQuotesToInstruments(
    quotes: Record<string, Quote>,
    groups: Record<string, DiscoverTopPerformerGroup>,
    groupIdentifier: string,
  ) {
    return Object.values(groups)
      .find((group) => group.name === groupIdentifier)
      ?.instruments.map((instrument) => mapQuoteToTopPerformerInstrument(quotes[instrument.isin], instrument))
      .filter((instrument) => instrument);
  }

  private subscribeToQuotes(isins: string[], groupIdentifier: string) {
    return this.#pushService.subscribeMultiBuffered(isins).pipe(
      filter((quotes) => !isEmpty(quotes)),
      concatLatestFrom(() => this.#store.select(discoverFeature.selectTopPerformerGroups)),
      map(([quotes, groups]) => this.mapQuotesToInstruments(quotes, groups, groupIdentifier)),
      map((instruments) => discoverActions.updateTopPerformerInstrumentsQuotes({ instruments })),
      catchError(() => of(discoverActions.subscribeToTopPerformerInstrumentsFailure())),
    );
  }

  private getTopPerformerInstruments(groupIdentifier: string) {
    return this.#store.select(selectDiscoverTopPerformerInstrumentISINsByGroup(groupIdentifier)).pipe(
      distinctUntilObjectChanged(),
      switchMap((isins) => this.subscribeToQuotes(isins, groupIdentifier)),
    );
  }

  public subscribeToTopPerformerInstruments$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(discoverActions.subscribeToTopPerformerInstruments),
      switchMap(({ groupIdentifier }) => this.getTopPerformerInstruments(groupIdentifier)),
    );
  });

  public unsubscribeFromTopPerformerInstruments$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(discoverActions.unsubscribeFromTopPerformerInstruments),
        concatLatestFrom(({ groupIdentifier }) => this.#store.select(selectDiscoverTopPerformerInstrumentISINsByGroup(groupIdentifier))),
        tap(([, isins]) => this.#pushService.unsubscribeMulti(isins)),
      );
    },
    { dispatch: false },
  );

  public loadMultiOrderStrategyAssets$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(discoverApiActions.loadMultiOrderStrategySuccess),
      concatLatestFrom(() => this.#store.select(discoverFeature.selectMultiOrderStrategy)),
      filter(([, strategy]) => strategy?.instrumentIsins?.length > 0),
      switchMap(([, strategy]) => {
        return this.#assetSearchService.loadAssetsByIsins(strategy?.instrumentIsins).pipe(
          map((assets) => discoverApiActions.loadMultiOrderStrategyAssetsSuccess({ assets })),
          catchError((error) => of(discoverApiActions.loadMultiOrderStrategyAssetsFailure({ error }))),
        );
      }),
    );
  });

  public subscribeToMultiOrderStrategyAssets$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(discoverApiActions.loadMultiOrderStrategyAssetsSuccess),
      concatLatestFrom(() => this.#store.select(discoverFeature.selectMultiOrderStrategy)),
      switchMap(([, strategy]) =>
        this.#pushService.subscribeMultiBuffered(strategy.instrumentIsins).pipe(
          filter((quotes) => !isEmpty(quotes)),
          map((quotes) => discoverActions.updateMultiOrderStrategyAssetQuotes({ quotes })),
        ),
      ),
    );
  });

  public handleBackendError$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(
          discoverApiActions.loadDiscoverOverviewFailure,
          discoverApiActions.loadDiscoverArticleDetailsFailure,
          discoverApiActions.loadMultiOrderStrategiesFailure,
          discoverApiActions.loadMultiOrderStrategyFailure,
          discoverApiActions.loadMultiOrderStrategyAssetsFailure,
        ),
        tap(({ error }) => this.#storeErrorHandler.handleBackendError(error)),
      );
    },
    { dispatch: false },
  );

  public loadMultiOrderStrategies$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(discoverActions.loadMultiOrderStrategies),
      switchMap(({ strategyType }) =>
        this.#discoverInfrastructure.getMultiOrderStrategyOverview().pipe(
          map((strategyOverview) => {
            let strategies = strategyOverview?.[strategyType] || [];
            strategies = strategies.sort((a, b) => a.sort - b.sort);
            return discoverApiActions.loadMultiOrderStrategiesSuccess({ strategies });
          }),
          catchError((error) => of(discoverApiActions.loadMultiOrderStrategiesFailure({ error }))),
        ),
      ),
    );
  });

  public loadMultiOrderStrategy$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(discoverActions.loadMultiOrderStrategy),
      switchMap(({ id }) =>
        this.#discoverInfrastructure.getMultiOrderStrategy(id).pipe(
          map((strategy) => discoverApiActions.loadMultiOrderStrategySuccess({ strategy })),
          catchError((error) => of(discoverApiActions.loadMultiOrderStrategyFailure({ error }))),
        ),
      ),
    );
  });

  private mapDiscoverOverviewToVm(overview: DiscoverOverview): DiscoverOverviewVM {
    const { indices, todayTeaser, listsAndLinks, knowHow, megatrends, topPerformerGroups } = overview;

    const indicesRecord = indices?.reduce<Record<string, DiscoverIndexVM>>((acc, index) => ({ ...acc, [index.isin]: index }), {});
    const todayTeaserRecord = todayTeaser?.reduce<Record<string, DiscoverTeaserVM>>((acc, teaser) => ({ ...acc, [teaser.id]: teaser }), {});
    const listsAndLinksRecord = listsAndLinks?.reduce<Record<string, DiscoverListsAndLinksVM>>(
      (acc, listAndLink) => ({ ...acc, [listAndLink.id]: listAndLink }),
      {},
    );
    const knowHowRecord = knowHow?.reduce<Record<string, DiscoverTeaserVM>>((acc, knowHow) => ({ ...acc, [knowHow.id]: knowHow }), {});
    const megatrendsRecord = megatrends?.reduce<Record<string, DiscoverTeaserVM>>(
      (acc, megatrend) => ({ ...acc, [megatrend.id]: megatrend }),
      {},
    );
    const topPerformerGroupsRecord = topPerformerGroups?.reduce<Record<string, DiscoverTopPerformerGroupVM>>(
      (acc, topPerformerGroups) => ({ ...acc, [topPerformerGroups.name]: topPerformerGroups }),
      {},
    );

    return {
      indices: indicesRecord,
      todayTeaser: todayTeaserRecord,
      listsAndLinks: listsAndLinksRecord,
      knowHow: knowHowRecord,
      megatrends: megatrendsRecord,
      topPerformerGroups: topPerformerGroupsRecord,
    } satisfies DiscoverOverviewVM;
  }

  private mapDiscoverInstrumentsToRecord(instruments: DiscoverInstrument[]): Record<string, DiscoverInstrumentVM> {
    return instruments
      ? instruments.reduce<Record<string, DiscoverInstrumentVM>>((acc, instrument) => {
          acc[instrument.isin] = instrument;
          return acc;
        }, {})
      : {};
  }

  private mapInstrumentListToVM(details: DiscoverListAndLinksDetails): DiscoverListAndLinksDetailsVM {
    const { segments, instruments, ...rest } = details;

    if (segments?.length) {
      const instrumentsRecord = segments.reduce<Record<string, DiscoverListAndLinksDetailsInstrumentVM>>((acc, segment) => {
        const segmentInstruments = segment.instruments.map((instrument) => ({ ...instrument, segment: segment.name.toLowerCase() }));
        segmentInstruments.forEach((instrument) => {
          if (acc[instrument.isin]) {
            acc[instrument.isin].segment.push(instrument.segment);
          } else {
            acc[instrument.isin] = { ...instrument, segment: [instrument.segment] };
          }
        });
        return acc;
      }, {});

      return {
        ...rest,
        instruments: instrumentsRecord,
        segments,
      } satisfies DiscoverListAndLinksDetailsVM;
    } else {
      const instrumentsRecord = instruments
        ? instruments.reduce<Record<string, DiscoverListAndLinksDetailsInstrumentVM>>((acc, instrument) => {
            acc[instrument.isin] = { ...instrument, segment: null };
            return acc;
          }, {})
        : {};
      return {
        ...rest,
        instruments: instrumentsRecord,
      } as DiscoverListAndLinksDetailsVM;
    }
  }
}

const mapQuoteToInstrument = <T extends DiscoverIndexVM | DiscoverInstrumentVM>(rawQuote: Quote, instrument: T): T => {
  if (!rawQuote) {
    return null;
  }

  if (rawQuote.time === instrument.quoteTime || !rawQuote.bid) {
    return null;
  }
  const quote = rawQuote?.bid ?? 0;
  const quoteTime = rawQuote.time;

  const historicQuote = instrument.historicQuote?.bid ?? 0;
  const perfAbs = historicQuote === 0 ? 0 : quote - historicQuote;
  const perfPct = historicQuote === 0 ? 0 : ((quote - historicQuote) / historicQuote) * 100;

  return {
    ...instrument,
    quote,
    quoteTime,
    perfAbs,
    perfPct,
  } as T;
};

const mapQuoteToTopPerformerInstrument = (
  rawQuote: Quote,
  instrument: DiscoverTopPerformerInstrumentVM,
): DiscoverTopPerformerInstrumentVM => {
  if (!rawQuote?.bid) {
    return null;
  }

  const quote = rawQuote?.bid ?? 0;
  const historicQuote = instrument.historicQuote?.bid ?? 0;
  const perfPct = historicQuote === 0 ? 0 : ((quote - historicQuote) / historicQuote) * 100;
  return {
    ...instrument,
    quote: rawQuote,
    perfPct,
  } as DiscoverTopPerformerInstrumentVM;
};
